import { Link } from "react-router-dom";
import { Store, Percent, Truck, Users, ShieldCheck, LineChart, ChevronRight, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";

const HERO = "https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=1600";

const PERKS = [
  { icon: Users, title: "A nationwide member audience", body: "Paying members who already want ranch-direct meat — you show up in one unified marketplace and in their drop notifications." },
  { icon: Store, title: "Your own storefront", body: "Your name, your logo, your story, your prices. Customers click straight through to your ranch's page." },
  { icon: Truck, title: "You ship, you stay in control", body: "You manage inventory, pack orders, and ship directly. We never touch, repackage, or resell your product." },
  { icon: LineChart, title: "Payouts, statements, reports", body: "See every order and payout in your Rancher Dashboard. Download statements any time." },
  { icon: ShieldCheck, title: "Standards that protect your brand", body: "Every rancher is reviewed. Customers know a Farm To You badge means real sourcing and real food-safety compliance." },
];

const STEPS = [
  ["Apply", "Tell us about your operation, practices, processing partners, and shipping capabilities."],
  ["Review", "Our team reviews your application and documentation, usually within 5 business days."],
  ["Build your storefront", "Add your logo, story, products, boxes, and shipping rules in the Rancher Dashboard."],
  ["Start selling", "Products go live once approved. Schedule limited drops, fulfill orders, and get paid."],
];

export default function SellOnFarmToYou() {
  return (
    <div data-testid="sell-page">
      <Seo title="Sell on The Farm To You — Rancher partners" description="List and sell your beef, pork, poultry, lamb, or seafood to members nationwide. Keep your brand, ship direct, simple commission." path="/sell"/>
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img src={HERO} alt="Cattle on open pasture" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/10"/>
        <div className="relative max-w-7xl mx-auto px-6 pb-16 pt-32 text-white">
          <p className="overline text-[var(--ff-accent-kraft)]">Become a selling partner</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight max-w-3xl">Your ranch. Our marketplace.<br/>Customers nationwide.</h1>
          <p className="mt-5 text-white/85 max-w-2xl text-base md:text-lg">The Farm To You connects independent American ranchers and farmers with members who want to buy directly from the source. You list, you price, you ship — we bring the buyers and the checkout.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/partners/apply" className="btn-citrus" data-testid="sell-apply-cta">Apply to sell <ChevronRight size={16}/></Link>
            <Link to="/legal/vendor-agreement" className="btn-secondary !text-white !border-white/40 hover:!bg-white/10" data-testid="sell-agreement-link">Read the Rancher Agreement</Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="sell-perks">
        <p className="overline">Why ranchers sell here</p>
        <h2 className="font-display text-3xl sm:text-4xl mt-2 max-w-2xl leading-tight">Built for producers, not dropshippers.</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PERKS.map(p => { const I = p.icon; return (
            <div key={p.title} className="card-flat p-6">
              <div className="w-11 h-11 rounded-full bg-[var(--ff-primary)] text-white flex items-center justify-center"><I size={18}/></div>
              <h3 className="font-display text-xl mt-4">{p.title}</h3>
              <p className="text-sm text-[var(--ff-text-secondary)] mt-2 leading-relaxed">{p.body}</p>
            </div>); })}
        </div>
      </section>

      <section className="bg-[var(--ff-muted)] py-20" data-testid="sell-steps">
        <div className="max-w-7xl mx-auto px-6">
          <p className="overline">How onboarding works</p>
          <h2 className="font-display text-3xl sm:text-4xl mt-2">From application to first sale.</h2>
          <div className="mt-10 grid md:grid-cols-4 gap-6">
            {STEPS.map(([t, b], i) => (
              <div key={t} className="bg-white rounded-2xl border border-[var(--ff-border)] p-6">
                <span className="font-display text-4xl text-[var(--ff-accent-kraft)]">0{i + 1}</span>
                <h3 className="font-display text-xl mt-3">{t}</h3>
                <p className="text-sm text-[var(--ff-text-secondary)] mt-2">{b}</p>
              </div>))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-start" data-testid="sell-responsibilities">
        <div>
          <p className="overline">What we ask of ranchers</p>
          <h2 className="font-display text-3xl mt-2">Marketplace fulfillment standards.</h2>
          <ul className="mt-6 space-y-3 text-sm">
            {["Maintain accurate inventory and pricing", "Prepare and package products to our cold-chain standards", "Comply with applicable USDA/state food-safety and shipping requirements", "Ship directly to customers within your stated window", "Provide tracking numbers for every shipment", "Respond to customer reviews and support requests promptly"].map(x => <li key={x} className="flex gap-2"><CheckCircle2 size={16} className="text-[var(--ff-primary)] shrink-0 mt-0.5"/> {x}</li>)}
          </ul>
        </div>
        <div className="card-flat p-8 text-white" style={{ background: "var(--ff-primary)" }}>
          <p className="overline text-[var(--ff-accent-kraft)]">What Farm To You handles</p>
          <ul className="mt-4 space-y-3 text-sm text-white/90">
            {["The customer-facing marketplace and storefronts", "Membership program and member benefits", "Single checkout and payment processing", "Platform policies, trust badges, and dispute process", "Marketing, drop notifications, and the overall customer experience"].map(x => <li key={x} className="flex gap-2"><CheckCircle2 size={16} className="text-[var(--ff-accent-kraft)] shrink-0 mt-0.5"/> {x}</li>)}
          </ul>
          <Link to="/partners/apply" className="btn-citrus mt-8 inline-flex" data-testid="sell-apply-cta-bottom">Start your application <ChevronRight size={16}/></Link>
          <p className="mt-4 text-xs text-white/70">Ranchers may not sell until approved by a Farm To You administrator. See our <Link to="/legal/seller-standards" className="underline">Seller Standards</Link> and <Link to="/legal/prohibited-products" className="underline">Prohibited Products Policy</Link>.</p>
        </div>
      </section>
    </div>
  );
}
